import { z } from 'zod';
import type { ReplicateClient } from '../replicate';
import { pollPrediction } from '../replicate';
import { getLlmModel } from '@/shared/catalog/models';

export interface LlmRunInput {
  modelId: string;
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
  /** Reference images. Replicate's Anthropic/OpenAI predictors accept a single `image` field, so only the first URL is forwarded. Ignored if the model is not vision-capable. */
  imageUrls?: readonly string[];
  signal?: AbortSignal;
}

export interface LlmRunOutput {
  text: string;
  inputTokens?: number;
  outputTokens?: number;
  predictTimeSec?: number;
  replicateId: string;
}

const StringOrArraySchema = z.union([z.string(), z.array(z.string())]);

/**
 * Runs an LLM model on Replicate and returns text + usage metrics.
 * Token counts are reported by Replicate metrics when the model exposes them.
 */
export async function runLlm(
  client: ReplicateClient,
  input: LlmRunInput,
): Promise<LlmRunOutput> {
  const model = getLlmModel(input.modelId);
  if (!model) throw new Error(`Unknown LLM model: ${input.modelId}`);

  const includeImage =
    model.supportsVision && input.imageUrls !== undefined && input.imageUrls.length > 0;
  const prediction = await client.createPrediction({
    model: input.modelId,
    input: {
      prompt: input.prompt,
      ...(input.systemPrompt !== undefined && { system_prompt: input.systemPrompt }),
      max_tokens: input.maxTokens ?? 4096,
      temperature: input.temperature ?? 0.4,
      ...(includeImage && { image: input.imageUrls![0] }),
    },
    ...(input.signal && { signal: input.signal }),
  });

  const final = await pollPrediction(client, prediction.id, {
    ...(input.signal && { signal: input.signal }),
  });

  if (final.status !== 'succeeded') {
    throw new Error(
      `LLM run failed: ${typeof final.error === 'string' ? final.error : 'unknown error'}`,
    );
  }

  const parsed = StringOrArraySchema.safeParse(final.output);
  const text = parsed.success
    ? Array.isArray(parsed.data)
      ? parsed.data.join('')
      : parsed.data
    : '';

  return {
    text,
    replicateId: final.id,
    ...(final.metrics?.input_token_count !== undefined && {
      inputTokens: final.metrics.input_token_count,
    }),
    ...(final.metrics?.output_token_count !== undefined && {
      outputTokens: final.metrics.output_token_count,
    }),
    ...(final.metrics?.predict_time !== undefined && {
      predictTimeSec: final.metrics.predict_time,
    }),
  };
}

/** Strips ```json fences and parses, useful when models wrap structured output. */
export function parseJsonLoose<T>(text: string, schema: z.ZodType<T>): T {
  const stripped = text
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```$/i, '')
    .trim();
  const json = JSON.parse(stripped) as unknown;
  return schema.parse(json);
}
